import { cva, cx, type VariantProps } from 'class-variance-authority';
import type { ClassValue } from 'class-variance-authority/types';

/**
 * A variant value the core knows about, PLUS any string an app invents.
 *
 * `(string & {})` is the standard trick for "these values are suggested, others
 * are legal": it keeps autocomplete on the known members while still accepting
 * an arbitrary string, so an app can add `tone="brand"` through the override
 * registry without the core's union having to learn about it first.
 */
export type Extensible<T extends string> = T | (string & {});

/** What an app may change about one recipe, without editing the core. */
export interface RecipeOverride {
    /**
     * `extend` (default) appends the app's class to the core's, so the core's
     * layout survives and the app only re-skins. `replace` drops the core class
     * for that slot entirely — reach for it when the core's own rule is what you
     * are fighting.
     */
    mode?: 'extend' | 'replace';
    /** Extra classes on the recipe's base, always applied. */
    base?: string;
    /**
     * Per-axis classes. A value the core does not define is simply ADDED, which
     * is how a new variant appears without a core release:
     *
     * ```ts
     * { callout: { variants: { tone: { brand: 'my-callout--brand' } } } }
     * ```
     */
    variants?: Record<string, Record<string, string>>;
}

/** Keyed by recipe name — see each component's `RECIPE` export. */
export type RecipeOverrides = Record<string, RecipeOverride>;

export interface RecipeContextValue {
    overrides: RecipeOverrides;
    /**
     * Appended to every recipe's base as `ui-theme-<name>`, so an app can ship a
     * whole alternate skin by scoping its CSS under one class instead of
     * overriding each recipe. `undefined` adds nothing.
     */
    theme?: string;
}

/** The props a recipe accepts — one optional value per declared axis. */
export type RecipeProps<V extends Record<string, Record<string, ClassValue>>> = {
    [K in keyof V]?: Extensible<Extract<keyof V[K], string>> | null | undefined;
};

export interface Recipe<V extends Record<string, Record<string, ClassValue>>> {
    /** Registry key an app's override map addresses. */
    name: string;
    /** Resolve to a class string, before any app override is applied. */
    resolve: (props?: RecipeProps<V>) => string;
    /** The declared axes, so an override can be validated in dev. */
    axes: V;
    base: string;
}

/**
 * Declare a component's variant → class mapping.
 *
 * WHY CVA AND NOT A TERNARY CHAIN. Once a component has two or more variant
 * axes the hand-written version becomes a nest of conditionals that no longer
 * says what it means — the matrix checkbox had `locked ? (state === 'off' ? …)`
 * three levels deep for what is really a 3 × 2 table. CVA states the table,
 * gives `defaultVariants` and `compoundVariants` for the awkward corners, and
 * infers the prop types from the declaration so the two cannot drift.
 *
 * WHY A REGISTRY ON TOP OF CVA. `cva` alone bakes the class map in at module
 * scope, which means adding a tone or reskinning one means editing this package
 * and cutting a release. `defineRecipe` names the recipe so an app can extend or
 * replace any slot through `UIVariantsProvider` — new variants and whole themes
 * become app-side changes.
 *
 * These are CLASS NAMES, not styles: `packages/ui` ships no CSS, so a recipe
 * emits stable `ui-*` hooks and the consuming app decides what they look like.
 * Components keep their inline `useUIColors()` defaults, so an app that styles
 * nothing still renders correctly — the classes are an opt-in seam, not a
 * requirement.
 */
export function defineRecipe<V extends Record<string, Record<string, ClassValue>>>(
    name: string,
    base: string,
    config: Parameters<typeof cva<V>>[1]
): Recipe<V> {
    const fn = cva<V>(base, config);
    return {
        name,
        base,
        axes: (config?.variants ?? {}) as V,
        // `Extensible` lets an app pass a value the core never declared (that is
        // the whole point of the override registry); cva returns just the base
        // for it, and `applyOverride` supplies the app's class.
        resolve: (props) => fn(props as Parameters<typeof fn>[0]) ?? '',
    };
}

/**
 * Apply an app's override on top of a recipe's own output.
 *
 * Pure so it can be unit-tested and so `useRecipe` stays a one-liner; callers
 * inside components should use the hook.
 */
export function applyOverride(
    recipe: Recipe<Record<string, Record<string, ClassValue>>>,
    resolved: string,
    props: Record<string, unknown> | undefined,
    ctx: RecipeContextValue
): string {
    const override = ctx.overrides[recipe.name];
    const themeClass = ctx.theme ? `ui-theme-${ctx.theme}` : undefined;
    if (!override) return cx(resolved, themeClass);

    const extras: string[] = [];
    for (const [axis, values] of Object.entries(override.variants ?? {})) {
        const chosen = props?.[axis];
        if (typeof chosen !== 'string') continue;
        const cls = values[chosen];
        if (cls) extras.push(cls);
    }

    // `replace` still keeps the app's own classes and the theme — it drops only
    // what the core contributed, which is the whole point of asking for it.
    const core = override.mode === 'replace' ? '' : resolved;
    return cx(core, override.base, extras, themeClass);
}

export { cx };
export type { VariantProps, ClassValue };
